<template>
  <transition name="filter-slide">
    <div v-if="visible" class="filter-overlay" @click.self="handleClose" tabindex="-1">
      <div class="filter-panel" :style="{ width: width + 'px' }" role="dialog" aria-modal="true">
        <header class="filter-header">
          <h3 class="filter-title">{{ title || $t("common.filter") }}</h3>
          <button class="close-btn" type="button" :title="$t('common.close')" @click="handleClose">×</button>
        </header>
        <section class="filter-body">
          <slot />
        </section>
        <footer class="filter-footer">
          <SmagriaButton class="primary" :label="$t('common.apply')" :action="handleApply" :customClass="'primary'" />
          <SmagriaButton
            class="reset"
            :label="$t('common.reset_filters')"
            :action="handleReset"
            :customClass="'reset'"
          />
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
export default {
  name: "GridFilter",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    width: {
      type: Number,
      default: 420,
    },
    gridRef: {
      type: Object,
      default: null,
    },
  },
  components: {
    SmagriaButton,
  },
  mounted() {
    document.addEventListener("keydown", this.onEsc);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEsc);
  },
  methods: {
    handleClose() {
      if (this.$listeners && this.$listeners.close) {
        this.$emit("close");
        return;
      }

      const parent = this.$parent;
      if (parent && typeof parent.closeFilterPanel === "function") {
        parent.closeFilterPanel();
        return;
      }

      this.$emit("update:visible", false);
    },
    handleApply() {
      if (this.$listeners && this.$listeners.apply) {
        this.$emit("apply");
        return;
      }

      const parent = this.$parent;
      if (parent && typeof parent.applyFilters === "function") {
        parent.applyFilters();
        return;
      }

      const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
      if (instance) {
        instance.refresh();
      }
      this.handleClose();
    },
    handleReset() {
      if (this.$listeners && this.$listeners.reset) {
        this.$emit("reset");
        return;
      }

      const parent = this.$parent;
      if (parent && typeof parent.resetFilters === "function") {
        parent.resetFilters();
        return;
      }

      const instance = this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
      if (instance) {
        instance.clearFilter();
        instance.refresh();
      }

      // If parent has a method to reset filters in the component state, call it to reset any custom filter values as well
      if (parent && typeof parent.callAfterResetFilters === "function") {
        parent.callAfterResetFilters();
      }

      this.handleClose();
    },
    onEsc(e) {
      if (!this.visible) return;
      if (e.key === "Escape" || e.key === "Esc") {
        this.handleClose();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/global/colors.scss";

.filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 2000;
}

.filter-panel {
  background: #fff;
  height: 100%;
  max-width: 100%;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  overflow-y: auto;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.filter-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: $gray-90;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: $gray-60;
  width: 32px;
  height: 32px;
  border-radius: 16px;
}

.close-btn:hover,
.close-btn:focus-visible {
  background: rgba(0, 0, 0, 0.05);
  outline: none;
}

.filter-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

:deep(.filter-field) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
}

:deep(.filter-field label) {
  color: $gray-70;
  font-weight: 600;
}

:deep(.filter-field input),
:deep(.filter-field select),
:deep(.filter-field .dx-texteditor-input) {
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 14px;
  color: $gray-90;
}

:deep(.filter-field input :not(.no-border)),
:deep(.filter-field select :not(.no-border)),
:deep(.filter-field .dx-texteditor-input :not(.no-border)) {
  border-bottom: 1px solid $gray-30;
}

:deep(.filter-field input:focus),
:deep(.filter-field select:focus),
:deep(.filter-field .dx-texteditor-input:focus) {
  outline: none;
  border-color: $brand;
}

.filter-footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
}

.filter-slide-enter-active,
.filter-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.filter-slide-enter,
.filter-slide-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
</style>

<style>
/* Ensure DxTagBox dropdown appears above GridFilter modal scale-filter-users-tagbox */
.dx-tagbox-popup-wrapper {
  z-index: 2001 !important;
}
.dx-tagbox-popup-wrapper .dx-overlay-content {
  z-index: 2001 !important;
}

/* Ensure DxDateBox popup appears above GridFilter modal */
.dx-datebox-popup-wrapper {
  z-index: 2001 !important;
}
.dx-datebox-popup-wrapper .dx-overlay-content {
  z-index: 2001 !important;
}
.dx-popup-wrapper {
  z-index: 2001 !important;
}
.dx-popup-wrapper .dx-overlay-content {
  z-index: 2001 !important;
}
</style>
